import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { IconButton, Popover, makeStyles, useMediaQuery, useTheme } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";
import { i18n } from "../../translate/i18n";
import { QUICK_REACTIONS, sameEmoji } from "./reactions";

// Barra rápida de reações do WhatsApp (seis emojis + "mais", que abre o seletor completo).
const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    paper: {
      borderRadius: 28,
      backgroundColor: isDark ? "#233138" : "#ffffff",
      backgroundImage: "none",
      boxShadow: "0 2px 5px rgba(11,20,26,0.26), 0 2px 10px rgba(11,20,26,0.16)",
      overflow: "hidden",
      maxWidth: "calc(100vw - 16px)",
    },
    paperPicker: {
      borderRadius: 12,
      "& .emoji-mart": { border: 0, maxWidth: "calc(100vw - 16px)" },
    },
    bar: {
      display: "flex",
      alignItems: "center",
      gap: 2,
      padding: "6px 8px",
    },
    emojiBtn: {
      width: 40,
      height: 40,
      padding: 0,
      border: 0,
      borderRadius: "50%",
      background: "transparent",
      cursor: "pointer",
      fontSize: 26,
      lineHeight: "40px",
      fontFamily: '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif',
      transition: "transform 120ms ease, background-color 120ms ease",
      "&:hover": { transform: "scale(1.18)" },
      "&:focus-visible": { outline: "2px solid #00a884", outlineOffset: -2 },
      "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } },
      [theme.breakpoints.down("xs")]: { width: 36, height: 36, fontSize: 23, lineHeight: "36px" },
    },
    emojiBtnMine: {
      backgroundColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(11,20,26,0.09)",
    },
    more: {
      width: 36,
      height: 36,
      padding: 0,
      marginLeft: 2,
      borderRadius: "50%",
      color: isDark ? "#aebac1" : "#54656f",
      backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(11,20,26,0.06)",
      "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.14)" : "rgba(11,20,26,0.1)" },
    },
  };
});

const pickerLabels = () => ({
  search: i18n.t("conversation.emojiPicker.search"),
  notfound: i18n.t("conversation.emojiPicker.notFound"),
  categories: {
    search: i18n.t("conversation.emojiPicker.results"),
    recent: i18n.t("conversation.emojiPicker.recent"),
    people: i18n.t("conversation.emojiPicker.people"),
    nature: i18n.t("conversation.emojiPicker.nature"),
    foods: i18n.t("conversation.emojiPicker.foods"),
    activity: i18n.t("conversation.emojiPicker.activity"),
    places: i18n.t("conversation.emojiPicker.places"),
    objects: i18n.t("conversation.emojiPicker.objects"),
    symbols: i18n.t("conversation.emojiPicker.symbols"),
    flags: i18n.t("conversation.emojiPicker.flags"),
  },
});

const ReactionPicker = ({ anchorEl, open, mine, startOnPicker = false, onPick, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [showAll, setShowAll] = useState(startOnPicker);

  const actionRef = useRef(null);

  useEffect(() => {
    if (open) setShowAll(startOnPicker);
  }, [open, startOnPicker]);

  // o seletor completo é bem maior que a barra: recalcula a posição ao trocar
  useEffect(() => {
    if (open && actionRef.current) actionRef.current.updatePosition();
  }, [open, showAll]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      action={actionRef}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      classes={{ paper: clsx(classes.paper, showAll && classes.paperPicker) }}
      elevation={0}
    >
      {showAll ? (
        <Picker
          perLine={isMobile ? 8 : 9}
          theme={theme.palette.type === "dark" ? "dark" : "light"}
          i18n={pickerLabels()}
          showPreview={false}
          showSkinTones={false}
          native
          onSelect={(e) => onPick(e.native)}
        />
      ) : (
        <div className={classes.bar} role="group" aria-label={i18n.t("conversation.reactions.react")}>
          {QUICK_REACTIONS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className={clsx(classes.emojiBtn, mine && sameEmoji(mine, emoji) && classes.emojiBtnMine)}
              aria-label={emoji}
              aria-pressed={!!mine && sameEmoji(mine, emoji)}
              onClick={() => onPick(emoji)}
            >
              {emoji}
            </button>
          ))}
          <IconButton
            className={classes.more}
            aria-label={i18n.t("conversation.reactions.more")}
            title={i18n.t("conversation.reactions.more")}
            onClick={() => setShowAll(true)}
          >
            <AddIcon />
          </IconButton>
        </div>
      )}
    </Popover>
  );
};

export default ReactionPicker;
